// SPDX-License-Identifier: AGPL-3.0-or-later

import {usePressable} from '@app/features/app/hooks/usePressable';
import {CLOSE_DESCRIPTOR, GO_BACK_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import {Slider} from '@app/features/ui/components/Slider';
import {CaretLeftIcon, CaretRightIcon, XIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet.module.css';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {createContext, useCallback, useContext, useState} from 'react';

export type MenuActionEvent =
	| React.MouseEvent<Element>
	| React.KeyboardEvent<Element>
	| {
			readonly shiftKey?: boolean;
	  };
export type MenuActionHandler = {bivarianceHack(event?: MenuActionEvent): void}['bivarianceHack'];

export interface MenuItemType {
	id?: string;
	icon?: React.ReactNode;
	label: string;
	subtext?: string;
	onClick: MenuActionHandler;
	danger?: boolean;
	disabled?: boolean;
	hint?: string;
	shortcut?: React.ReactNode;
	closeOnSelect?: boolean;
	/** A shorter label under the round quick action on phones (Copy for Copy message). */
	quickLabel?: string;
	/** The round quick action is ink (Ask Konvo's sparkle), not the card colour. */
	quickInk?: boolean;
}

export interface MenuSubmenuItemType {
	id?: string;
	icon?: React.ReactNode;
	label: string;
	/** The current choice, quiet before the chevron ("2 of 3", "Mine"). */
	hint?: string;
	items: Array<MenuSheetItem>;
	disabled?: boolean;
	onTriggerSelect?: () => void;
	/** A shorter label under its quick button in the phone glass menu (Mute for Mute DM). */
	quickLabel?: string;
}

export interface MenuSliderType {
	label: string;
	value: number;
	minValue: number;
	maxValue: number;
	onChange: (value: number) => void;
	onFormat?: (value: number) => string;
	factoryDefaultValue?: number;
	step?: number;
}

export interface MenuCheckboxType {
	icon?: React.ReactNode;
	label: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
	/** A switch for showing and hiding things (Calendar layers); a checkbox by default. */
	appearance?: 'checkbox' | 'switch';
	/** Quiet extras before the control, such as a count or a badge. */
	trailing?: React.ReactNode;
}

export interface MenuRadioType {
	label: string;
	subtext?: string;
	selected: boolean;
	onSelect: () => void;
	disabled?: boolean;
}

export type MenuLeafItem = MenuItemType | MenuSliderType | MenuCheckboxType | MenuRadioType;
export type MenuSheetItem = MenuLeafItem | MenuSubmenuItemType;

export interface MenuGroupType {
	items: Array<MenuSheetItem>;
	/** A section of its own (an integration such as Konvo or ClickUp): titled at the top of its card. */
	title?: string;
	/** The section's logo beside its title. */
	titleIcon?: React.ReactNode;
}

export interface MenuBottomSheetProps {
	isOpen: boolean;
	onClose: () => void;
	/** What the menu acts on (a mail's subject, a chat's name), left-aligned at the top. */
	title?: string;
	/** A quieter second line under the title (sender and date, say). */
	subtitle?: string;
	/** An avatar or icon beside the title. */
	leading?: React.ReactNode;
	groups: Array<MenuGroupType>;
	headerContent?: React.ReactNode;
	showCloseButton?: boolean;
	/**
	 * Ids of up to four actions to lift out of the groups into a row of round buttons at the top,
	 * in this order (Reply, Reply all, Forward, Pin). Ids the menu doesn't have are skipped.
	 */
	quickIds?: ReadonlyArray<string>;
	/** How many quick actions the row takes (four by default; the message sheet fits five). */
	quickMax?: number;
	/**
	 * `tiles`: the WhatsApp attach sheet's look. The sheet sits on the bottom edge, the title row has a
	 * round close, the quick actions are rounded tiles with their label under and the rest sits in
	 * soft cards. `list` (the default) is the floating lens.
	 */
	look?: 'list' | 'tiles';
}

const MAX_QUICK_ACTIONS = 4;

/** The tiles look shows an action's hint (a count, the current choice) quietly at the row's end. */
const ShowHintsContext = createContext(false);

function submenuKey(item: MenuSubmenuItemType): string {
	return item.id ?? item.label;
}

function findSubmenu(groups: Array<MenuGroupType>, key: string): MenuSubmenuItemType | null {
	for (const group of groups) {
		for (const item of group.items) {
			if ('items' in item && submenuKey(item) === key) return item;
		}
	}
	return null;
}

/** Lifts the quick actions out of the groups; groups left empty are dropped. Shared with desktop menus. */
export function splitQuickActions(
	groups: Array<MenuGroupType>,
	quickIds: ReadonlyArray<string> | undefined,
	max: number = MAX_QUICK_ACTIONS,
): {quick: Array<MenuItemType>; rest: Array<MenuGroupType>} {
	if (!quickIds || quickIds.length === 0) return {quick: [], rest: groups};
	const byId = new Map<string, MenuItemType>();
	for (const group of groups) {
		for (const item of group.items) {
			if ('onClick' in item && !('items' in item) && item.id && quickIds.includes(item.id) && item.icon) {
				byId.set(item.id, item as MenuItemType);
			}
		}
	}
	const quick = quickIds
		.map((id) => byId.get(id))
		.filter((item): item is MenuItemType => item != null)
		.slice(0, max);
	if (quick.length === 0) return {quick, rest: groups};
	const lifted = new Set(quick);
	const rest = groups
		.map((group) => ({...group, items: group.items.filter((item) => !lifted.has(item as MenuItemType))}))
		.filter((group) => group.items.length > 0);
	return {quick, rest};
}

const QuickAction: React.FC<{item: MenuItemType}> = observer(({item}) => {
	const {isPressed, pressableProps} = usePressable(item.disabled);
	return (
		<button
			type="button"
			onClick={item.onClick}
			disabled={item.disabled}
			className={clsx(
				styles.quickAction,
				isPressed && styles.quickActionPressed,
				item.danger && styles.danger,
				item.quickInk && styles.quickActionInk,
			)}
			aria-label={item.label}
			data-flx="ui.menu-bottom-sheet.quick-action"
			{...pressableProps}
		>
			<span className={styles.quickActionCircle} aria-hidden="true">
				{item.icon}
			</span>
			<span className={styles.quickActionLabel} aria-hidden="true">
				{item.quickLabel ?? item.label}
			</span>
		</button>
	);
});

const TileAction: React.FC<{item: MenuItemType}> = observer(({item}) => {
	const {isPressed, pressableProps} = usePressable(item.disabled);
	return (
		<button
			type="button"
			onClick={item.onClick}
			disabled={item.disabled}
			className={clsx(styles.tileAction, isPressed && styles.tileActionPressed, item.danger && styles.danger)}
			aria-label={item.label}
			data-flx="ui.menu-bottom-sheet.tile-action"
			{...pressableProps}
		>
			<span className={styles.tile} aria-hidden="true">
				{item.icon}
			</span>
			<span className={styles.tileLabel} aria-hidden="true">
				{item.quickLabel ?? item.label}
			</span>
		</button>
	);
});

const MenuCheckboxItem: React.FC<{item: MenuCheckboxType; isLast: boolean}> = observer(({item, isLast}) => {
	const {isPressed, pressableProps} = usePressable(item.disabled);
	return (
		<>
			<button
				type="button"
				role="checkbox"
				aria-checked={item.checked}
				aria-label={item.label}
				onClick={() => item.onChange(!item.checked)}
				disabled={item.disabled}
				className={clsx(styles.menuItem, item.disabled && styles.disabled, isPressed && styles.pressed)}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.menu-item.change.button"
				{...pressableProps}
			>
				{item.icon && (
					<div
						className={styles.iconContainer}
						aria-hidden="true"
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.icon-container"
					>
						{item.icon}
					</div>
				)}
				<span className={styles.label} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.label">
					{item.label}
				</span>
				{item.trailing ? (
					<span className={styles.trailing} data-flx="ui.menu-bottom-sheet.menu-checkbox-item.trailing">
						{item.trailing}
					</span>
				) : null}
				{item.appearance === 'switch' ? (
					<span
						className={clsx(styles.switchTrack, item.checked && styles.switchOn)}
						aria-hidden="true"
						data-flx="ui.menu-bottom-sheet.menu-checkbox-item.switch"
					>
						<span className={styles.switchThumb} />
					</span>
				) : (
					<div
						className={styles.checkboxContainer}
						aria-hidden="true"
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.checkbox-container"
					>
						<div
							className={clsx(styles.checkbox, item.checked && styles.checked)}
							data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.checkbox"
						>
							{item.checked && (
								<svg
									className={styles.checkIcon}
									viewBox="0 0 12 12"
									fill="none"
									aria-hidden="true"
									data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.check-icon"
								>
									<path
										d="M10 3L4.5 8.5L2 6"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.path"
									/>
								</svg>
							)}
						</div>
					</div>
				)}
			</button>
			{!isLast && (
				<div className={styles.divider} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-checkbox-item.divider" />
			)}
		</>
	);
});
const MenuRadioItem: React.FC<{item: MenuRadioType; isLast: boolean}> = observer(({item, isLast}) => {
	const {isPressed, pressableProps} = usePressable(item.disabled);
	return (
		<>
			<button
				type="button"
				role="radio"
				aria-checked={item.selected}
				aria-label={item.label}
				onClick={item.onSelect}
				disabled={item.disabled}
				className={clsx(styles.menuItem, item.disabled && styles.disabled, isPressed && styles.pressed)}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.menu-item.select.button"
				{...pressableProps}
			>
				<div
					className={styles.radioContainer}
					aria-hidden="true"
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.radio-container"
				>
					<div
						className={clsx(styles.radio, item.selected && styles.radioSelected)}
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.radio"
					>
						{item.selected && (
							<div
								className={styles.radioInner}
								data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.radio-inner"
							/>
						)}
					</div>
				</div>
				<div
					className={styles.labelColumn}
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.label-column"
				>
					<span className={styles.label} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.label">
						{item.label}
					</span>
					{item.subtext && (
						<span className={styles.subtext} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.subtext">
							{item.subtext}
						</span>
					)}
				</div>
			</button>
			{!isLast && (
				<div className={styles.divider} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-radio-item.divider" />
			)}
		</>
	);
});
const MenuActionItem: React.FC<{item: MenuItemType; isLast: boolean}> = observer(({item, isLast}) => {
	const {isPressed, pressableProps} = usePressable(item.disabled);
	const showHints = useContext(ShowHintsContext);
	return (
		<>
			<button
				type="button"
				onClick={item.onClick}
				disabled={item.disabled}
				className={clsx(
					styles.menuItem,
					item.disabled && styles.disabled,
					item.danger && styles.danger,
					isPressed && styles.pressed,
					isPressed && item.danger && styles.pressedDanger,
				)}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.menu-item.click.button"
				{...pressableProps}
			>
				{item.icon && (
					<div
						className={styles.iconContainer}
						aria-hidden="true"
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.icon-container"
					>
						{item.icon}
					</div>
				)}
				{item.subtext ? (
					<div
						className={styles.labelColumn}
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.label-column"
					>
						<span className={styles.label} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.label">
							{item.label}
						</span>
						<span className={styles.subtext} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.subtext">
							{item.subtext}
						</span>
					</div>
				) : (
					<span className={styles.label} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.label--2">
						{item.label}
					</span>
				)}
				{showHints && item.hint ? (
					<span className={styles.trailing} data-flx="ui.menu-bottom-sheet.menu-action-item.hint">
						{item.hint}
					</span>
				) : null}
			</button>
			{!isLast && (
				<div className={styles.divider} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-action-item.divider" />
			)}
		</>
	);
});
const MenuSliderItem: React.FC<{item: MenuSliderType; isLast: boolean}> = observer(({item, isLast}) => {
	return (
		<>
			<div
				className={styles.sliderContainer}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-slider-item.slider-container"
			>
				<span
					className={styles.sliderLabel}
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-slider-item.slider-label"
				>
					{item.label}
				</span>
				<Slider
					defaultValue={item.value}
					factoryDefaultValue={item.factoryDefaultValue ?? item.value}
					minValue={item.minValue}
					maxValue={item.maxValue}
					onValueChange={item.onChange}
					onValueRender={item.onFormat}
					value={item.value}
					mini={true}
					step={item.step ?? 1}
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-slider-item.slider"
				/>
			</div>
			{!isLast && (
				<div className={styles.divider} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-slider-item.divider" />
			)}
		</>
	);
});
const MenuSubmenuItem: React.FC<{
	item: MenuSubmenuItemType;
	isLast: boolean;
	onExpand: (item: MenuSubmenuItemType) => void;
}> = observer(({item, isLast, onExpand}) => {
	const {isPressed, pressableProps} = usePressable(item.disabled);
	const handleClick = useCallback(() => {
		onExpand(item);
	}, [item, onExpand]);
	return (
		<>
			<button
				type="button"
				onClick={handleClick}
				disabled={item.disabled}
				className={clsx(styles.menuItem, item.disabled && styles.disabled, isPressed && styles.pressed)}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-submenu-item.menu-item.click.button"
				{...pressableProps}
			>
				{item.icon && (
					<div
						className={styles.iconContainer}
						aria-hidden="true"
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-submenu-item.icon-container"
					>
						{item.icon}
					</div>
				)}
				<span className={styles.label} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-submenu-item.label">
					{item.label}
				</span>
				{item.hint ? (
					<span className={styles.trailing} data-flx="ui.menu-bottom-sheet.menu-submenu-item.hint">
						{item.hint}
					</span>
				) : null}
				<CaretRightIcon
					size={remFromPx(20)}
					className={styles.submenuChevron}
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-submenu-item.submenu-chevron"
				/>
			</button>
			{!isLast && (
				<div className={styles.divider} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-submenu-item.divider" />
			)}
		</>
	);
});
const MenuItem: React.FC<{
	item: MenuItemType | MenuSliderType | MenuCheckboxType | MenuRadioType | MenuSubmenuItemType;
	isLast?: boolean;
	onExpandSubmenu?: (item: MenuSubmenuItemType) => void;
}> = observer(({item, isLast = false, onExpandSubmenu}) => {
	if ('checked' in item) {
		return (
			<MenuCheckboxItem
				item={item as MenuCheckboxType}
				isLast={isLast}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-item.menu-checkbox-item"
			/>
		);
	}
	if ('selected' in item) {
		return (
			<MenuRadioItem
				item={item as MenuRadioType}
				isLast={isLast}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-item.menu-radio-item"
			/>
		);
	}
	if ('items' in item && onExpandSubmenu) {
		return (
			<MenuSubmenuItem
				item={item as MenuSubmenuItemType}
				isLast={isLast}
				onExpand={onExpandSubmenu}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-item.menu-submenu-item"
			/>
		);
	}
	if ('onClick' in item) {
		return (
			<MenuActionItem
				item={item as MenuItemType}
				isLast={isLast}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-item.menu-action-item"
			/>
		);
	}
	return (
		<MenuSliderItem
			item={item as MenuSliderType}
			isLast={isLast}
			data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-item.menu-slider-item"
		/>
	);
});
const MenuGroup: React.FC<{
	group: MenuGroupType;
	isLast?: boolean;
	onExpandSubmenu?: (item: MenuSubmenuItemType) => void;
}> = observer(({group, isLast = false, onExpandSubmenu}) => {
	return (
		<>
			<div
				className={styles.groupContainer}
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-group.group-container"
			>
				{group.title ? (
					<div className={styles.groupTitle} data-flx="ui.menu-bottom-sheet.group-title">
						{group.titleIcon ? (
							<span className={styles.groupTitleIcon} aria-hidden="true">
								{group.titleIcon}
							</span>
						) : null}
						<span>{group.title}</span>
					</div>
				) : null}
				{group.items.map((item, index) => (
					<MenuItem
						key={`${'label' in item ? item.label : 'slider'}-${index}`}
						item={item}
						isLast={index === group.items.length - 1}
						onExpandSubmenu={onExpandSubmenu}
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-group.menu-item"
					/>
				))}
			</div>
			{!isLast && (
				<div className={styles.groupSpacer} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-group.group-spacer" />
			)}
		</>
	);
});
export const MenuBottomSheet: React.FC<MenuBottomSheetProps> = observer(
	({
		isOpen,
		onClose,
		title,
		subtitle,
		leading,
		groups,
		headerContent,
		showCloseButton = false,
		quickIds,
		quickMax,
		look = 'list',
	}) => {
		const {i18n} = useLingui();
		// The open submenu by name, read from the latest groups so its ticks follow the changes made in it.
		const [activeSubmenuKey, setActiveSubmenuKey] = useState<string | null>(null);
		const activeSubmenu = activeSubmenuKey == null ? null : findSubmenu(groups, activeSubmenuKey);
		const hasHeader = Boolean(title || headerContent);
		const {quick, rest} = splitQuickActions(groups, quickIds, quickMax);
		const handleExpandSubmenu = useCallback((item: MenuSubmenuItemType) => {
			setActiveSubmenuKey(submenuKey(item));
		}, []);
		const handleCloseSubmenu = useCallback(() => {
			setActiveSubmenuKey(null);
		}, []);
		const handleClose = useCallback(() => {
			setActiveSubmenuKey(null);
			onClose();
		}, [onClose]);
		if (activeSubmenu) {
			const submenuGroups: Array<MenuGroupType> = [{items: activeSubmenu.items}];
			const backButton = (
				<button
					type="button"
					onClick={handleCloseSubmenu}
					className={styles.backButton}
					aria-label={i18n._(GO_BACK_DESCRIPTOR)}
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.back-button.close-submenu"
				>
					<CaretLeftIcon size={remFromPx(20)} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.caret-left-icon" />
				</button>
			);
			// Menu sheets have no inputs and must not inherit a stale iOS keyboard offset.
			return (
				<BottomSheet
					isOpen={isOpen}
					onClose={handleClose}
					snapPoints={[0, 1]}
					fitContent
					pinToBottom
					initialSnap={1}
					title={activeSubmenu.label}
					showCloseButton={false}
					leadingAction={backButton}
					floating
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.bottom-sheet"
				>
					<div
						className={styles.bottomSheetContent}
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.bottom-sheet-content"
					>
						<div className={styles.groupStack} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.group-stack">
							{submenuGroups.map((group, index) => (
								<MenuGroup
									key={index}
									group={group}
									isLast={index === submenuGroups.length - 1}
									data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-group"
								/>
							))}
						</div>
					</div>
				</BottomSheet>
			);
		}
		if (look === 'tiles') {
			return (
				<BottomSheet
					isOpen={isOpen}
					onClose={handleClose}
					snapPoints={[0, 1]}
					fitContent
					pinToBottom
					initialSnap={1}
					disableDefaultHeader
					showCloseButton={false}
					disablePadding
					containerClassName={styles.tilesSheet}
					data-flx="ui.menu-bottom-sheet.tiles"
				>
					<div className={styles.tilesContent} data-flx="ui.menu-bottom-sheet.tiles.content">
						<div className={styles.tilesHeader} data-flx="ui.menu-bottom-sheet.header">
							{leading ? <span className={styles.menuHeaderLeading}>{leading}</span> : null}
							<span className={styles.menuHeaderText}>
								{title ? <span className={styles.menuTitle}>{title}</span> : null}
								{subtitle ? <span className={styles.menuSubtitle}>{subtitle}</span> : null}
							</span>
							<button
								type="button"
								className={styles.tilesClose}
								onClick={handleClose}
								aria-label={i18n._(CLOSE_DESCRIPTOR)}
								data-flx="ui.menu-bottom-sheet.tiles.close"
							>
								<XIcon size={16} weight="bold" />
							</button>
						</div>
						{headerContent && (
							<div className={styles.headerSlot} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.header-slot">
								{headerContent}
							</div>
						)}
						{quick.length > 0 ? (
							<div
								className={styles.tileRow}
								style={{'--tile-count': Math.max(quick.length, 3)} as React.CSSProperties}
								data-flx="ui.menu-bottom-sheet.tile-row"
							>
								{quick.map((item) => (
									<TileAction key={item.id} item={item} />
								))}
							</div>
						) : null}
						<div className={styles.groupStack} data-flx="ui.menu-bottom-sheet.tiles.groups">
							<ShowHintsContext.Provider value={true}>
								{rest.map((group, index) => (
									<MenuGroup
										key={index}
										group={group}
										isLast={index === rest.length - 1}
										onExpandSubmenu={handleExpandSubmenu}
									/>
								))}
							</ShowHintsContext.Provider>
						</div>
					</div>
				</BottomSheet>
			);
		}
		// Keep action menus attached to the physical bottom edge after the iOS keyboard closes.
		return (
			<BottomSheet
				isOpen={isOpen}
				onClose={handleClose}
				snapPoints={[0, 1]}
				fitContent
				pinToBottom
				initialSnap={1}
				// The title is drawn below, left-aligned with its avatar; the default header only for Close.
				title={showCloseButton ? title : undefined}
				showCloseButton={showCloseButton}
				disableDefaultHeader={!showCloseButton}
				floating
				data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.bottom-sheet--2"
			>
				<div
					className={styles.bottomSheetContent}
					data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.bottom-sheet-content--2"
				>
					{title && !showCloseButton ? (
						<div className={styles.menuHeader} data-flx="ui.menu-bottom-sheet.header">
							{leading ? <span className={styles.menuHeaderLeading}>{leading}</span> : null}
							<span className={styles.menuHeaderText}>
								<span className={styles.menuTitle}>{title}</span>
								{subtitle ? <span className={styles.menuSubtitle}>{subtitle}</span> : null}
							</span>
						</div>
					) : null}
					{headerContent && (
						<div className={styles.headerSlot} data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.header-slot">
							{headerContent}
						</div>
					)}
					{quick.length > 0 ? (
						<div className={styles.quickRow} data-flx="ui.menu-bottom-sheet.quick-row">
							{quick.map((item) => (
								<QuickAction key={item.id} item={item} />
							))}
						</div>
					) : null}
					<div
						className={clsx(styles.groupStack, hasHeader && quick.length === 0 && styles.groupStackWithHeader)}
						data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.group-stack--2"
					>
						{rest.map((group, index) => (
							<MenuGroup
								key={index}
								group={group}
								isLast={index === rest.length - 1}
								onExpandSubmenu={handleExpandSubmenu}
								data-flx="ui.menu-bottom-sheet.menu-bottom-sheet.menu-group--2"
							/>
						))}
					</div>
				</div>
			</BottomSheet>
		);
	},
);

MenuBottomSheet.displayName = 'MenuBottomSheet';
